import { medicationApi } from 'src/api/medication';
import { treatmentApi } from 'src/api/treatment';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import { GetProfileMedicationsDto, GetProfileTreatmentDto } from 'woofwoof-api';
import { useClinicalQuery } from './useClinicalQuery';

export interface ActivityMedicalScope {
  medications: GetProfileMedicationsDto[] | undefined;
  treatments: GetProfileTreatmentDto[] | undefined;
}

/**
 * Medication/treatment source for the visit modal's Przebieg tab (M-4
 * follow-up): the patient's own lists when the visit has a registered
 * patient — an ADDED/UPDATED activity entry may name a medication or
 * treatment created during a *different* visit of the same patient, which
 * the visit-only list never holds — the visit-scoped lists on a guest visit,
 * which has no profile to query. Mirrors `VisitCourseCard`'s guest/patient
 * split and reuses its cache keys (`patient-medications`,
 * `profile-treatments`) so an edit here invalidates the same cache other
 * patient views read. Every answer carries the viewing clinic's Warehouse
 * data, so each key is the selected organization's (R1-1, `useClinicalQuery`).
 */
export function useActivityMedicalScope(
  visitId: string,
  patientId: string | undefined,
): ActivityMedicalScope {
  const isGuestVisit = !patientId;

  const { data: patientMedicationsData } = useClinicalQuery(
    'patient-medications',
    patientId,
    [],
    (options) =>
      medicationApi.medicationControllerGetProfileMedicationsInOrganization(
        { profileId: patientId || '' },
        options,
      ),
    { enabled: !isGuestVisit && Boolean(patientId) },
  );
  const { data: visitMedicationsData } = useClinicalQuery(
    'visit-medications-modal',
    visitId,
    [],
    (options) => visitScopedPrescribingApi.listMedications(visitId, options),
    { enabled: isGuestVisit && Boolean(visitId) },
  );

  const { data: patientTreatmentsData } = useClinicalQuery(
    'profile-treatments',
    patientId,
    [],
    (options) =>
      treatmentApi.treatmentRecordControllerGetProfileTreatments(
        { profileId: patientId || '' },
        options,
      ),
    { enabled: !isGuestVisit && Boolean(patientId) },
  );
  const { data: visitTreatmentsData } = useClinicalQuery(
    'visit-treatments-modal',
    visitId,
    [],
    (options) => visitScopedPrescribingApi.listTreatments(visitId, options),
    { enabled: isGuestVisit && Boolean(visitId) },
  );

  return {
    medications: isGuestVisit
      ? visitMedicationsData?.data
      : patientMedicationsData?.data.data,
    treatments: isGuestVisit
      ? visitTreatmentsData?.data
      : patientTreatmentsData?.data.data,
  };
}
